import { memo, useCallback, useEffect, useRef } from 'react';
import { useSignal } from '@preact/signals-react';
import { DropDownWrapper, MenuItem } from 'components';
import { ArrowDropDown } from 'assets/icons';
import ReactCountryFlag from 'react-country-flag';
import * as S from '../MetricCardHeader.styled';

function getCountryCode(country) {
  if (country === 'UK') return 'GB';
  return country;
}

const flagStyle = {
  width: '20px',
  height: '15px',
  marginRight: '8px',
  marginBottom: '2px',
};

export const CountrySelect = memo(({ countries, selectedCountry, onCountryChange }) => {
  const selectRef = useRef(null);
  const isOpen$ = useSignal(false);
  const selectedCountryData = countries.find((country) => country.code === selectedCountry);

  const closeOnOutsideClick = useCallback(
    (event) => {
      if (selectRef.current?.contains(event.target)) return;

      isOpen$.value = false;
    },
    [isOpen$],
  );

  const toggleDropDown = useCallback(() => {
    isOpen$.value = !isOpen$.value;
  }, [isOpen$]);

  const closeDropDown = useCallback(() => {
    isOpen$.value = false;
  }, [isOpen$]);

  const handleCountryChange = useCallback(
    (value) => {
      onCountryChange(value);
      isOpen$.value = false;
    },
    [isOpen$, onCountryChange],
  );

  useEffect(() => {
    document.addEventListener('click', closeOnOutsideClick, true);
    return () => document.removeEventListener('click', closeOnOutsideClick, true);
  }, [closeOnOutsideClick]);

  return (
    <S.SelectWrap ref={selectRef} style={{ flexShrink: '0' }}>
      <S.SelectButton type='button' onClick={toggleDropDown} style={{ width: '130px' }}>
        <S.SelectValue>
          {selectedCountryData && (
            <ReactCountryFlag countryCode={getCountryCode(selectedCountry)} svg style={flagStyle} />
          )}
          {selectedCountryData?.name || 'All Countries'}
        </S.SelectValue>
        <S.SelectIcon>
          <ArrowDropDown />
        </S.SelectIcon>
      </S.SelectButton>
      <DropDownWrapper
        isOpen$={isOpen$}
        width={154}
        targetRef={selectRef}
        style={{ top: '36px', right: '0' }}
        outSideClickAction={closeDropDown}
      >
        <MenuItem
          value=''
          height={32}
          style={{ minWidth: 'unset' }}
          isSelected={!selectedCountry}
          onClick={handleCountryChange}
        >
          All Countries
        </MenuItem>
        {countries.map((country) => (
          <MenuItem
            key={country.code}
            value={country.code}
            height={32}
            style={{ minWidth: 'unset' }}
            isSelected={country.code === selectedCountry}
            onClick={handleCountryChange}
          >
            <ReactCountryFlag countryCode={getCountryCode(country.code)} svg style={flagStyle} />
            {country.name}
          </MenuItem>
        ))}
      </DropDownWrapper>
    </S.SelectWrap>
  );
});

CountrySelect.displayName = 'CountrySelect';
